import React, { useCallback, useEffect, useRef, useState } from "react";
import Icon from "../../YourAIAgents/components/components/icon";
import { Tooltip } from "@mui/material";
import { Link, useLocation, useNavigate } from "react-router-dom";
import SidebarChatLink from "./SidebarChatLink";
import { useUsersContext } from "../../../context/usersContext";

const ChatHistorySidebar = () => {
  const [isCloseNav, setIsCloseNav] = useState(false);
  const {
    fetchChats,
    conversations,
    setSelectedAssistant,
    setChatError,
    chatError,
  } = useUsersContext();
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const loaderRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const isTasksRoute = location.pathname.startsWith("/app/chat/tasks");

  const handleOpenCreateModal = () => navigate("/app/chat/tasks/new");

  const getChats = async () => {
    setLoading(true);
    await fetchChats(page);
    setLoading(false);
  };

  useEffect(() => {
    getChats();
  }, [page]);

  const handleObserver = useCallback(
    (entries) => {
      const target = entries[0];
      if (target.isIntersecting && conversations.hasMore && !loading) {
        setPage((prev) => prev + 1);
      }
    },
    [conversations],
  );

  useEffect(() => {
    const option = {
      root: null,
      rootMargin: "200px",
      threshold: 0,
    };
    if (conversations.hasMore) {
      const observer = new IntersectionObserver(handleObserver, option);

      if (loaderRef.current) {
        observer.observe(loaderRef.current);
      }
      return () => observer.disconnect();
    }
  }, [handleObserver]);

  return (
    <div
      className={`${isCloseNav ? "w-[79px]" : "w-[250px]"} border-r h-full`}
      style={{ fontFamily: "Inter" }}
    >
      <div
        className={
          isCloseNav
            ? ""
            : "h-[calc(100%-80px)] overflow-y-auto v3chat__content"
        }
      >
        <div
          className={
            isCloseNav
              ? "flex flex-col px-5 py-6 gap-4  items-center"
              : "flex justify-between px-3.5 py-5 items-center"
          }
        >
          <div onClick={() => setIsCloseNav(!isCloseNav)}>
            <Tooltip
              title={isCloseNav ? "Open Sidebar" : "Close Sidebar"}
              placement={isCloseNav ? "left" : "right"}
            >
              <span className="cursor-pointer">
                <Icon id="tooglesidebar" />
              </span>
            </Tooltip>
          </div>

          <div
            className={isCloseNav ? "flex flex-col gap-4" : "flex gap-6 px-3"}
          >
            <div>
              <Link to={"search"}>
                <Tooltip title="Search" placement="left">
                  <span className="cursor-pointer">
                    <Icon id="v3_search" />
                  </span>
                </Tooltip>
              </Link>
            </div>

            <div
              onClick={() => {
                if (window.location.pathname !== "/app/chat") {
                  setSelectedAssistant(null);
                  navigate("/app/chat");
                  chatError?.showError &&
                    setChatError({
                      showError: false,
                      errorMessage: "",
                      classNames: "",
                      iconColor: "",
                    });
                }
              }}
            >
              <Tooltip title="New Task" placement="left">
                <span className="cursor-pointer">
                  <Icon id="plus" />
                </span>
              </Tooltip>
            </div>
          </div>
        </div>
        {!isCloseNav && (
          <>


            {isTasksRoute && (
              <button
                type="button"
                className="text-white cursor-pointer mx-4 bg-black rounded-[5px] h-[42px] w-[85%] flex items-center justify-center text-[12px] font-medium"
                onClick={handleOpenCreateModal}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-sparkles w-4 h-4"><path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z"></path><path d="M20 3v4"></path><path d="M22 5h-4"></path><path d="M4 17v2"></path><path d="M5 18H3"></path></svg>
                <span className="text-[14px] ms-2">Create Custom Task</span>
              </button>
            )}
            <p className="text-[16px] pl-3.5 mt-5 mb-2 text-[#4a4a4a] font-[500] text-capitalize">
              Task History
            </p>
            <div>
              {conversations?.data?.favChats?.length > 0 &&
                conversations?.data?.favChats?.map((chat) => (
                  <SidebarChatLink item={chat} key={chat.conversation_id} />
                ))}
            </div>
            <div>
              <p className="text-[16px] pl-3.5 mt-5 mb-2 text-[#4a4a4a] font-[500]">
                Today
              </p>
              {conversations?.data?.today?.length > 0 &&
                conversations?.data?.today?.map((chat) => (
                  <SidebarChatLink
                    fav={false}
                    item={chat}
                    key={chat.conversation_id}
                  />
                ))}
            </div>
            <div>
              <p className="text-[16px] pl-3.5 mt-5 mb-2 text-[#4a4a4a] font-[500]">
                Yesterday
              </p>
              {conversations?.data?.yesterday?.length > 0 &&
                conversations?.data?.yesterday?.map((chat) => (
                  <SidebarChatLink
                    fav={false}
                    item={chat}
                    key={chat.conversation_id}
                  />
                ))}
            </div>
            <div>
              <p className="text-[16px] pl-3.5 mt-5 mb-2 text-[#4a4a4a] font-[500]">
                Previous 7 Days
              </p>
              {conversations?.data?.last7Day?.length > 0 &&
                conversations?.data?.last7Day?.map((chat) => (
                  <SidebarChatLink
                    fav={false}
                    key={chat.conversation_id}
                    item={chat}
                  />
                ))}
            </div>
          </>
        )}
        <div ref={loaderRef} className="h-[40px] text-center w-full">
          {loading && "Loading..."}
        </div>
      </div>
      {isCloseNav ? (
        <div
          className="flex items-center justify-center cursor-pointer"
          onClick={() => navigate("/app/settings", { state: { tab: "Team" } })}
        >
          <Tooltip title="Invite Team Members" placement="right">
            <span>
              <Icon id={"AddTeamMember"} />
            </span>
          </Tooltip>
        </div>
      ) : (
        <div
          className="my-2.5 mx-0.5 p-4 w-fll border cursor-pointer"
          onClick={() => navigate("/app/settings", { state: { tab: "Team" } })}
        >
          <span>
            <Icon id={"AddTeamMember"} />
            <span className="ml-2.5 !font-[400]">Invite Team Members </span>
          </span>
        </div>
      )}

    </div>
  );
};

export default ChatHistorySidebar;
